const Instructions = require('./CH2Instructions');
const Analytics = require('./CH2Analytics');

class CH2VirtualTryOn {
    constructor(_document, _window, _navigator, _supportedArType, _autostart = false) {
        this.document = _document;
        this.window = _window;
        this.navigator = _navigator;
        this.pid = null;
        this.vtoMode = null;
        this.tryOnButton = null;
        this.agreeButton = null;
        this.termsWindow = null;
        this.virtualTryOnARModal = null;
        this.virtualTryOnARModalBg = null;
        this.infoButton = null;
        this.closeInfoButton = null;
        this.animationContainer = null;
        this.arAnimation = null;
        this.privacyDeniedButton = null;
        this.flipCardContainer = null;
        this.imgLogo4Qrcode = null;
        this.analytics = null;
        this.autostart = _autostart;
        this.supportedArType = _supportedArType;
        // Event handlers
        this.boundHandlePackageLoaded = null;
        this.boundOpenAnimationHandler = null;
        this.boundCloseAnimationHandler = null;
        this.init();
    }

    init() {
        this.pid = this.document.querySelector('.product-detail.product-wrapper').getAttribute('data-pid');
        this.flipCardContainer = this.document.querySelector('.ch2_flip-cards-container');
        this.termsWindow = this.document.querySelector('.ch2_vto-ar-terms');
        this.vtoAnimationWrapper = this.document.querySelector('.ch2_vto-animation-wrapper');
        this.flipCardModalHead = this.document.querySelector('.ch2_flip-card-modal-head');

        this.tryOnButton = this.document.getElementById('ch2_button-tryon');
        this.agreeButton = this.document.getElementById('ch2_accept-ar-privacy');
        this.virtualTryOnARModal = this.document.getElementById('ch2_modal-vto-ar');
        this.virtualTryOnARModalBg = this.document.getElementById('ch2_bg-modal-vto-ar');
        this.infoButton = this.document.getElementById('ch2_flip-card-info');
        this.closeInfoButton = this.document.getElementById('ch2_close-info-animation');
        this.animationContainer = this.document.getElementById('ch2_vto-animation-container');
        this.analytics = new Analytics();
        this.addOpenCloseEventListeners();
        this.run();
    }

    /**
     * Creates an image logo for a QR code and passes it to the specified callback.
     * If the logo has already been created, the existing logo will be passed to the callback.
     *
     * @param {function} callback - The function to pass the created logo to.
     */
    createImageLogo4Qrcode(callback) {
        if (!this.imgLogo4Qrcode && typeof callback === 'function') {
            const img = new Image();
            img.onload = function () {
                const canvas = document.createElement('canvas');
                canvas.width = this.width;
                canvas.height = this.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(this, 0, 0);
                const dataURL = canvas.toDataURL('image/png');
                const imgLogo = document.createElement('img');
                imgLogo.width = this.width;
                imgLogo.height = this.height;
                imgLogo.src = dataURL;
                callback(imgLogo);
            };
            img.setAttribute('crossOrigin', 'anonymous');
            img.src = `${this.window.staticURL}images/icon_qrcode.jpg`;
        }
    }

    /**
     * Adds event listeners for the virtual try-on animation.
     * Starts the VTO animation when the "Info" icon button is clicked by adding the ".vto-animation-show" class to the modal-vto-ar
     * and showing the animation.
     * When the "Close" button is clicked, it removes the ".ch2_vto-animation-show" class, hides the animation, and sets focus to the "Close" button again.
     */
    addAnimationEventListeners() {
        // Event listener for showing the virtual try-on animation
        this.boundOpenAnimationHandler = this.openAnimationHandler.bind(this);
        this.infoButton.addEventListener('click', this.boundOpenAnimationHandler);
        // Event listener for hiding the virtual try-on animation
        this.boundCloseAnimationHandler = this.closeAnimationHandler.bind(this);
        this.closeInfoButton.addEventListener('click', this.boundCloseAnimationHandler);
    }

    openAnimationHandler() {
        const vtoAnimation = {
            container: this.animationContainer,
            renderer: 'svg',
            loop: true,
            path: `${this.window.staticURL}images/animatedIcons/vtoAnimation.json`,
        };
        this.virtualTryOnARModal.classList.add('ch2_vto-animation-show');

        this.vtoAnimationWrapper.classList.remove('ch2_hidden');
        this.flipCardContainer.classList.add('ch2_hidden');
        this.flipCardModalHead.classList.add('ch2_hidden');
        this.infoButton.classList.add('ch2_hidden');

        this.arAnimation = this.window.lottie.loadAnimation(vtoAnimation);
        if (this.arAnimation) {
            this.arAnimation.play();
        }
    }

    closeAnimationHandler() {
        this.virtualTryOnARModal.classList.remove('ch2_vto-animation-show');

        this.vtoAnimationWrapper.classList.add('ch2_hidden');
        this.flipCardContainer.classList.remove('ch2_hidden');
        this.flipCardModalHead.classList.remove('ch2_hidden');
        this.infoButton.classList.remove('ch2_hidden');

        if (this.arAnimation) {
            this.arAnimation.stop();
            this.arAnimation = null;

            this.animationContainer.innerHTML = '';
        }
    }

    removeAnimationEventListeners() {
        this.infoButton.removeEventListener('click', this.boundOpenAnimationHandler);
        this.closeInfoButton.removeEventListener('click', this.boundCloseAnimationHandler);
    }

    /**
     * Opens the terms window by adding the ".active" class to the "Try on" icon button and the ".open" class to the terms window.
     * It also sets the focus to the "Agree" button and makes the slider image not tabbable.
     */
    openTerms() {
        this.tryOnButton.classList.add('ch2_active-btn');
        this.termsWindow.classList.add('ch2_modal-open');
    }

    /**
     * Closes the terms window by removing the ".open" class from the terms window.
     * It also makes the slider image tabbable again.
     * If the "DISAGREE" button was clicked, it removes the ".active" class from the "Try on" icon button.
     * If the "AGREE" button was clicked, it adds the ".active_modal" class to the virtual try-on AR modal and removes the ".hidden" class from the virtual try-on AR modal and its background.
     * @param {bool} privacyDenied The user denied her/his consent to the privacy policy.
     */
    closeTerms(privacyDenied) {
        // Make slider image tabbable again
        const currentSlide = this.document.querySelector(
            '.pdp-carousel-slick .slick-current',
        );
        if (currentSlide) {
            currentSlide.setAttribute('tabindex', '0');
        }
        this.termsWindow.classList.remove('ch2_modal-open');
        if (privacyDenied) {
            this.tryOnButton.classList.remove('ch2_active-btn');
        } else {
            switch (this.vtoMode) {
            case 'WebAR':
                this.virtualTryOnARModal.innerHTML = '';
                this.loadWebVTOFrame(this.virtualTryOnARModalBg, this.pid, false);
                this.tryOnButton.classList.add('ch2_active-btn');
                break;
            case 'AppleAR':
                this.virtualTryOnARModal.classList.add('ch2_active-modal');
                this.virtualTryOnARModal.classList.remove('ch2_hidden');
                this.virtualTryOnARModalBg.classList.remove('ch2_hidden');
                break;
            default:
                // eslint-disable-next-line no-console
                console.error('Change2:CH2TryOn:CloseTerms - Error invalid vtoMode');
                this.analytics.logBulgariError('Change2 - Error invalid vtoMode');
            }
        }
    }

    /**
     * Adds event listeners to the disagree, agree, and close buttons, which call the handleClose method
     * with the appropriate argument.
     */
    addOpenCloseEventListeners() {
        this.agreeButton.addEventListener('click', () => {
            this.handleClose(false);
        });
    }

    /**
     * Initializes the flip cards by adding a click and focus event listener to the ".ch2_flip-card-wrapper" class.
     * When a card is clicked, it removes the ".ch2_flip_active" class from all sibling cards and adds the class to the clicked card.
     */
    initFlipCards() {
        const flipCardWrappers = document.querySelectorAll('.ch2_flip-card-wrapper');
        const handleClickOrFocus = (e) => {
            flipCardWrappers.forEach((wrapper) => {
                if (wrapper !== e.currentTarget) {
                    wrapper.classList.remove('ch2_flip-active');
                }
            });
            e.currentTarget.classList.add('ch2_flip-active');
        };
        flipCardWrappers.forEach((wrapper) => {
            wrapper.addEventListener('click', handleClickOrFocus);
            wrapper.addEventListener('focus', handleClickOrFocus);
        });
    }

    /**
    * Returns a string representing the VTO mode: "AppleAR" for USDZ AR on iOS devices, "WebAR" for all devices.
    * @returns {string} Type of available VTO: 'AppleAR' or 'WebAR'
    * */
    getVtoMode() {
        switch (this.window.CH2Product.vtoModelVersion) {
        case 'CH2WEB':
        case 'WANNAVFR':
            return 'WebAR';
        default:
            return 'AppleAR';
        }
    }

    /**
     * Loads the VTO (Virtual Try-On) script by creating a script element with the appropriate src attribute and appending it to the head element of the HTML document.
     * If a script element with the ID "ch2_try-on-ar-script" already exists, it removes the existing script element before creating and appending a new one.
     * The script is only loaded if the following conditions are met:
     * - The window.CH2Product.vtoVersion property is not empty
     * - The product ID (pid) is defined
     * - VTO is enabled for the product
     * - A VTO model is defined for the product
     */
    loadScript() {
        const existingScript = this.document.getElementById('ch2_try-on-ar-script');
        if (existingScript) {
            existingScript.remove();
        }
        const pid = window.CH2Product.id;
        const vtoVersion = window.CH2Product.vtoVersion;
        const vtoModel = this.window.CH2Product.vtoModel;
        const vtoEnabled = this.window.CH2Product.vtoEnabled;
        if (vtoVersion.length > 0 && pid && vtoEnabled && vtoModel) {
            const change2script = this.document.createElement('script');
            change2script.src = `https://static.bulgari.com/3d/VTO/${vtoVersion}/include.js?SKU=${pid}&V=${vtoModel}`;
            change2script.id = 'ch2_try-on-ar-script';
            this.document.head.appendChild(change2script);
        }
    }

    /**
     * Closes the virtual try-on modal and sets the appropriate CSS classes based on the `privacyDenied` argument.
     *
     * @param {boolean} privacyDenied - A boolean value indicating whether privacy was denied by the user.
     */
    handleClose(privacyDenied) {
        this.virtualTryOnARModal.classList.remove('ch2_active-modal');
        this.tryOnButton.classList.remove('ch2_active-btn');
        this.virtualTryOnARModal.classList.add('ch2_hidden');
        this.virtualTryOnARModalBg.classList.add('ch2_hidden');

        this.closeTerms(privacyDenied);
    }

    /**
     * Creates a link card element with a 3D model as the source and an image as the front-facing card image.
     * @param {string} src3dModel - The URL of the 3D model to be linked.
     * @param {string} cardImage - The URL of the image to be displayed on the front of the card.
     * @return {HTMLElement} - The newly created link card element.
     */
    createLinkCard(src3dModel, cardImage) {
        const linkCardWrapper = this.document.createElement('div');
        linkCardWrapper.classList.add('ch2_link-card-wrapper');
        linkCardWrapper.innerHTML = `
            <div class="ch2_flip-card">
            <a href="${src3dModel}" rel="ar" target="_self">
                <img src="${cardImage}" class="card-front-img" alt="">
            </a>
            </div>
        `;
        return linkCardWrapper;
    }

    /**
     * Creates a flip card element with a QR code image on the front and additional content on the back.
     * @param {number} countQrcode - The ID of the QR code content element.
     * @param {string} cardImage - The URL of the QR code image to be displayed on the front of the card.
     * @return {HTMLElement} - The newly created flip card element.
     */
    createQrCodeCard(countQrcode, cardImage) {
        // TODO: Add support for other languages and warning span
        const qrCodeCardWrapper = this.document.createElement('div');
        qrCodeCardWrapper.classList.add('ch2_flip-card-wrapper');
        qrCodeCardWrapper.tabIndex = 0;
        qrCodeCardWrapper.setAttribute('aria-label', 'QR Code for the Virtual Try On, iOS only');
        const flipCard = this.document.createElement('div');
        flipCard.classList.add('ch2_flip-card');
        const frontCard = this.document.createElement('div');
        frontCard.classList.add('ch2_flip-card-front');
        const img = this.document.createElement('img');
        img.setAttribute('src', cardImage);
        img.setAttribute('alt', 'QR Code for the Virtual Try On, iOS only');
        frontCard.appendChild(img);
        const backCard = this.document.createElement('div');
        backCard.classList.add('ch2_flip-card-back');
        const qrCodeContent = this.document.createElement('div');
        qrCodeContent.setAttribute('id', countQrcode);
        qrCodeContent.classList.add('ch2_qrcode-content');
        const warningSpan = this.document.createElement('span');
        warningSpan.innerHTML = this.document.querySelector('[data-tryon-warning]').getAttribute('data-tryon-warning'); // Probably to be changed (!!!)
        backCard.appendChild(qrCodeContent);
        backCard.appendChild(warningSpan);
        flipCard.appendChild(frontCard);
        flipCard.appendChild(backCard);
        qrCodeCardWrapper.appendChild(flipCard);
        return qrCodeCardWrapper;
    }

    /**
     * Handles the loading of a package configuration by creating flip cards with links to 3D models or QR codes.
     * @param {Object} event - The event object containing the package configuration data.
     */
    handlePackageLoaded(event) {
        const packageConfig = event.detail;
        if (
            !packageConfig ||
            !packageConfig.ArEnabled ||
            !packageConfig.ArResouces.length
        ) {
            return;
        }
        this.flipCardContainer.innerHTML = '';

        let countQrCode = 0;
        packageConfig.ArResouces.forEach((resource) => {
            const src3dModel = resource.fileUrl;
            const cardImage = resource.photoUrl;

            countQrCode++;
            const idQrCode = `ch2_qrcode-card-${countQrCode}`;

            if (src3dModel.indexOf('.reality') > 0) {
                const card =
                    this.supportedArType === 'quickLook'
                        ? this.createLinkCard(src3dModel, cardImage)
                        : this.createQrCodeCard(idQrCode, cardImage); // TODO: QuickLook should open directly instead of preview mode

                this.flipCardContainer.appendChild(card);
                $(`#${idQrCode}`).qrcode({
                    render: 'canvas',
                    size: 1110,
                    quiet: 2,
                    minVersion: 8,
                    fill: '#000',
                    text: src3dModel,
                    mode: 4,
                    mSize: 0.25,
                    mPosX: 0.5,
                    mPosY: 0.5,
                    ecLevel: 'H',
                    image: this.imgLogo4Qrcode,
                });
            }
        });
        this.initFlipCards();
    }

    /**
     * @param {HTMLElement} HtmlContainer - The element to which the instructions will be appended
     * Open Instruction Interface
     */
    openInstruction(HtmlContainer) {
        const instruction = new Instructions();
        instruction.createInstructionInterface(
            HtmlContainer,
            this.window,
            this.navigator,
            'wannaVTO',
        );
        this.analytics.logEvent('instructions', null, null);
    }

    /**
     * Creates a QR code element wrapped in a div.
     * @param {string} QrcodeId - The ID of the QR code content element.
     * @return {HTMLElement} - The newly created element/wrapper.
     */
    createQrCodeVTO(QrcodeId) {
        // TODO: Add support for other languages and warning span
        const qrCodeCardWrapper = this.document.createElement('div');
        qrCodeCardWrapper.tabIndex = 0;
        qrCodeCardWrapper.setAttribute(
            'aria-label',
            'QR Code for the Virtual Try On',
        );
        const qrCodeContent = this.document.createElement('div');
        qrCodeContent.setAttribute('id', QrcodeId);
        qrCodeContent.classList.add('wanna_qrcode-content');
        qrCodeCardWrapper.appendChild(qrCodeContent);
        return qrCodeCardWrapper;
    }

    /**
     * Load the Web VTO in an iframe or dispalys a QR code for mobile
     * @param {HTMLElement} element - The element to which the iframe will be appended
     * @param {string} pid - The product ID
     * @param {boolean} showFpsCounter - Whether to show the FPS counter or not
     * @return {void}
     */
    loadWebVTOFrame(element, pid, showFpsCounter) {
        const existingIframe = this.document.getElementById('ch2_vto-frame');
        if (existingIframe) {
            element.removeChild(existingIframe);
        }

        const useAutoLoadQR = this.supportedArType === 'qrCode';
        let vtoProdUrl;
        let vtoDevUrl;

        const vtoFrame = document.createElement('iframe');
        vtoFrame.id = 'ch2_vto-frame';
        vtoFrame.allow = 'camera; autoplay; encrypted-media; web-share';

        // eslint-disable-next-line default-case
        switch (this.window.CH2Product.vtoModelVersion) {
        case 'CH2WEB':
            vtoProdUrl = `https://change2.io/bulgari_3d_assets/VTO_SFCC/index.html?PID=${pid}&FPSC=${showFpsCounter}`;
            vtoDevUrl = `https://localhost/VTO/index.html?PID=${pid}&FPSC=${showFpsCounter}`;
            element.classList.add('ch2_full-screen');
            break;
        case 'WANNAVFR':
            if (!useAutoLoadQR) {
                vtoProdUrl = `https://bulgari.ar.wanna.fashion/?modelid=${pid}`; // Name needs to be in this format!
                // If the model is called otherwise eg. name_number, WANNA technology considers the number as the diameter of the dial and breaks..
                // vtoDevUrl = `https://bulgari.ar.wanna.fashion/?modelid=103868&showonboarding=wristmeasurement&wristmeasurement=true`;
                vtoDevUrl = `https://bulgari.ar.wanna.fashion/?modelid=103868`;
                element.classList.add('ch2_full-screen');
                vtoFrame.addEventListener('load', function () {
                    const divButton = document.createElement('div');
                    divButton.setAttribute('id', 'wanna_close_vto');
                    divButton.addEventListener('click', function () {
                        element.removeChild(vtoFrame);
                        element.removeChild(divButton);
                        this.virtualTryOnARModalBg.classList.add('ch2_hidden');
                        this.tryOnButton.classList.remove('ch2_active-btn');
                    }.bind(this));
                    element.appendChild(divButton);
                }.bind(this));
            } else {
                // eslint-disable-next-line no-unused-vars
                vtoDevUrl = '';
                vtoProdUrl = '';

                // Instruction button & event
                const existingInstructionButton = this.document.getElementById('wanna_instruction');
                if (existingInstructionButton) {
                    element.removeChild(existingInstructionButton);
                }
                const instructionButton = document.createElement('button');
                instructionButton.id = 'wanna_instruction';
                instructionButton.innerHTML = '<svg role="img" viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g stroke="currentColor"><path d="m24 12.5c0 6.352-5.149 11.5-11.5 11.5-6.352 0-11.5-5.148-11.5-11.5 0-6.351 5.148-11.5 11.5-11.5 6.351 0 11.5 5.149 11.5 11.5z" /><path d="m12.5 17.5v-7.579h-2.526" stroke-linecap="round" stroke-linejoin="round" /><path d="m9.9736 17.5h5.053" stroke-linecap="round" stroke-linejoin="round" /></g><path d="m12.5 6.1318c0 .349-.282.631-.632.631-.349 0-.632-.282-.632-.631 0-.35.283-.632.632-.632.35 0 .632.282.632.632" fill="currentColor" /><path d="m12.5 6.1318c0 .349-.282.631-.632.631-.349 0-.632-.282-.632-.631 0-.35.283-.632.632-.632.35 0 .632.282.632.632z" stroke="currentColor" stroke-linejoin="round" /></g></svg>';
                instructionButton.addEventListener('click', function () {
                    this.openInstruction(element);
                }.bind(this));
                element.appendChild(instructionButton);

                // Close button & event
                element.classList.add('wanna_vto-QR');
                const existingClosebutton = this.document.getElementById('wanna_close_qr');
                if (existingClosebutton) {
                    element.removeChild(existingClosebutton);
                }
                const closeButton = document.createElement('button');
                closeButton.setAttribute('id', 'wanna_close_qr');
                closeButton.innerHTML = `<svg role="img" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"> <path d="m14 14.7-7-7-7 7-.7-.7 7-7-7-7 .7-.7 7 7 7-7 .7.7-7 7 7 7z" fill="currentColor" /> <path d="M0 0l14 14m0-14L0 14" stroke="currentColor" fill="none" stroke-linecap="square" /></svg>`;
                closeButton.addEventListener('click', function () {
                    element.removeChild(vtoFrame);
                    element.removeChild(closeButton);
                    element.removeChild(instructionButton);
                    this.virtualTryOnARModalBg.classList.add('ch2_hidden');
                    this.tryOnButton.classList.remove('ch2_active-btn');
                }.bind(this));
                element.appendChild(closeButton);

                // QR container
                const targetUrl = `${window.location.href}?loadVTO=true`;
                const idQrCode = `wanna_qr`;
                const qrContainerElement = this.createQrCodeVTO(idQrCode);
                vtoFrame.addEventListener('load', function () {
                    const iframeDocument = vtoFrame.contentDocument || vtoFrame.contentWindow.document;
                    iframeDocument.body.appendChild(qrContainerElement);
                    // CSS Injection
                    iframeDocument.head.innerHTML =
                        "<style type='text/css'>" +
                        // eslint-disable-next-line quotes
                        ".wanna_qrcode-content{ width: 100%; height: 100%; position: relative;}" +
                        // eslint-disable-next-line quotes
                        ".wanna_qrcode-content > canvas{ position: absolute; top: 50%; left: 50%;transform: translate(-50%,-50%);}" +
                        // eslint-disable-next-line quotes
                        "</style>";
                    // QR creation
                    $(iframeDocument.getElementById(idQrCode)).qrcode({
                        render: 'canvas',
                        size: 200,
                        quiet: 2,
                        minVersion: 8,
                        fill: '#000',
                        text: targetUrl,
                        mode: 4,
                        mSize: 0.25,
                        mPosX: 0.5,
                        mPosY: 0.5,
                        ecLevel: 'H',
                        image: this.imgLogo4Qrcode,
                    });
                }.bind(this));
            }
            break;
        }

        vtoFrame.setAttribute('src', vtoProdUrl);
        element.classList.remove('ch2_hidden');
        element.appendChild(vtoFrame);
    }

    /**
     * Loads the Web VTO event handlers
     * @param {HTMLElement} element - The element to which the iframe will be appended
     * @return {void}
     * */
    loadWebVTOListeners(element) {
        const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent';
        const eventerHandler = window[eventMethod];
        const messageEvent = eventMethod === 'attachEvent' ? 'onmessage' : 'message';

        eventerHandler(messageEvent, function (e) {
            switch (e.origin) {
            case 'https://change2.io':
            case 'https://localhost':
                break;
            default:
                return;
            }
            if (e.data === 'ch2_close_vto' || e.message === 'ch2_close_vto') {
                element.classList.remove('ch2_full-screen');
                element.classList.add('ch2_hidden');
                element.removeChild(document.getElementById('ch2_vto-frame'));
                this.tryOnButton.classList.remove('ch2_active-btn');
            }
        }.bind(this));
    }

    /**
     * Runs the virtual try-on experience by loading the required script, adding event listeners, initializing flip cards,
     * and adding animation event listeners.
     */
    run() {
        if (this.tryOnButton) {
            this.vtoMode = this.getVtoMode();
            switch (this.vtoMode) {
            case 'WebAR':
                this.tryOnButton.classList.remove('ch2_hidden');
                this.loadWebVTOListeners(this.virtualTryOnARModalBg);
                this.createImageLogo4Qrcode(function (result) {
                    this.imgLogo4Qrcode = result;
                }.bind(this));
                if (this.autostart) {
                    this.handleClose(false);
                }
                break;
            case 'AppleAR':
                if (this.supportedArType !== 'sceneViewer') {
                    this.tryOnButton.classList.remove('ch2_hidden');
                    this.addAnimationEventListeners();
                    this.boundHandlePackageLoaded = this.handlePackageLoaded.bind(this);
                    this.window.addEventListener('BvlgariPackage:Loaded', this.boundHandlePackageLoaded);
                    this.createImageLogo4Qrcode(function (result) {
                        this.imgLogo4Qrcode = result;
                        this.loadScript();
                    }.bind(this));
                } else {
                    // We don't support Android AR
                    this.tryOnButton.classList.add('ch2_hidden');
                }
                break;
            default:
                // eslint-disable-next-line no-console
                console.error('Change2:CH2TryOn:Run - Error invalid vtoMode');
                this.analytics.logBulgariError('Change2 - Error invalid vtoMode');
            }
        }
    }

    destroy() {
        if (this.tryOnButton) {
            switch (this.vtoMode) {
            case 'WebAR':
                this.loadWebVTOListeners(this.virtualTryOnARModalBg);
                break;
            case 'AppleAR':
                this.removeAnimationEventListeners();
                this.window.removeEventListener('BvlgariPackage:Loaded', this.boundHandlePackageLoaded);
                break;
            default:
                // eslint-disable-next-line no-console
                console.error('Change2:CH2TryOn:Run - Error invalid vtoMode');
                this.analytics.logBulgariError('Change2 - Error invalid vtoMode');
            }
        }
    }
}

module.exports = CH2VirtualTryOn;
